import {Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {QuoteWrapper} from '../../models/quote/quote-wrapper';
import {QuoteService} from '../../models/quote/quote-service';
import {Employee} from '../../models/employee/employee.model';
import {generateUniqueId} from '../../utils/math-utils';
import {EnumPricingItemType} from '../../models/quote/pricing-item/pricing-item-type';
import {DevicePricingItem} from '../../models/quote/pricing-item/device-pricing-item';
import {DeviceConsumablePricingItem} from '../../models/quote/pricing-item/device-consumable-pricing-item';
import {PackPricingItem} from '../../models/quote/pricing-item/pack-pricing-item';
import {OptionPricingItem} from '../../models/quote/pricing-item/option-pricing-item';
import {ConsumablePricingItem} from '../../models/quote/pricing-item/consumable-pricing-item';
import {ExtraFee, toExtraFeePayload} from '../../models/quote/extra-fee';
import {CmcQuote} from '../../models/quote/cmc-quote';
import {EnumQuoteType} from '../../models/enum/enum-quote-type';
import {QuoteServiceService} from '../../quote-service/quote-service.service';
import {Store} from '@ngrx/store';
import {Article} from '../../models/quote/pricing-item/article';
import {PricingItem} from '../../models/quote/pricing-item/pricing-item';
import {EppRouterService} from '../../router/epp-router.service';
import {CustomGroup} from '../../models/quote/group/custom-group';
import {SynthesisViewConfig} from '../../models/synthesis/synthesis-view-config.model';
import {TranslateService} from '@ngx-translate/core';
import {ServiceConfig} from '../../models/referential/service-config.model';
import {EnumQuoteServiceType} from '../../models/enum/enum-quote-service-type';
import {EnumQuoteServiceSalesType} from '../../models/enum/enum-quote-service-sales-type';
import {BillingLine} from '../../models/billing-line/billing-line.model';
import {CustomerContractService} from '../../customer-contract/_services/customer-contract.service';
import {ProductsByServicesRequest} from '../../models/quote/products-by-services-request';
import {map} from 'rxjs/operators';
import {EnumClientSize} from '../../models/enum/enum-client-size';
import {OpportunityContextModel} from '../../models/opportunity-context/opportunity-context.model';
import {CmcProductManagerService} from '../../products/_services/cmc/cmc-product-manager.service';
import {CmcProtocol} from '../../models/cmc/cmc-protocol.model';
import {EnumCorridorColor} from '../../models/enum/enum-corridor-color';
import {EmployeeRoleEnum} from '../../models/employee/employee-role.enum';
import {
    DeviceConsumableWithSuppPricingItem
} from '../../models/quote/pricing-item/device-consumable-with-supp-pricing-item';
import {PricingItemMapperFactory} from '../../models/quote/pricing-item/mapper/factory/pricing-item-mapper.factory';
import {ServiceLine} from '../../models/quote/service-line';
import {CommercialOfferPersonalisation} from '../../models/commercial-offer/commercial-offer-personalisation';
import {EPPClientPrototypeMapper} from '../../utils/EPPClientPrototypeMapper';
import {CoreState} from '../../store/reducers';
import {QuoteUpdateAction} from '../../store/quote';
import {ProductManagerV2Service} from '../../products/_services/product-manager-v2.service';
import {ProductSearchRequestV2} from '../../models/quote/product-search-request-v2';
import {ProductV2} from '../../models/productv2/productv2.model';
import {EnumOpportunityType} from '../../models/enum/enum-opportunity-type';
import {Option} from '../../models/option/option.model';
import {EnumOptionGranularity} from '../../models/enum/enum.option-granularity';
import {OptionPricingItemMapper} from '../../models/quote/pricing-item/mapper/option-pricing-item.mapper';

@Injectable()
export abstract class QuoteManagerService {

    API_URL = '/apiQuoteManager/v1/';

    constructor(
        protected eppRouterService: EppRouterService,
        protected quoteServiceService: QuoteServiceService,
        protected store: Store<CoreState>,
        protected translateService: TranslateService,
        protected customerContractService: CustomerContractService,
        protected productManagerV2Service: ProductManagerV2Service,
        protected cmcProductManagerService: CmcProductManagerService,
        protected optionPricingItemMapper: OptionPricingItemMapper
    ) {
    }

    public abstract uploadQuote(wrapper: QuoteWrapper, employee: Employee, personalization?: CommercialOfferPersonalisation): Observable<any>;

    public abstract findQuoteById(quoteId: string): Observable<QuoteWrapper>;

    public abstract sendPdf(quoteId: string, commercialOfferPersonalisation: CommercialOfferPersonalisation): Observable<any>;

    protected static quoteWrapperToJSON(wrapper: QuoteWrapper, currentUserValue: Employee) {
        const serializedWrapper = {
            reference: wrapper.reference,
            totalWeeklyAmount: wrapper.totalWeeklyAmount,
            totalMonthlyAmount: wrapper.totalMonthlyAmount,
            serviceLines: wrapper.serviceLines.map(sl => QuoteManagerService.serviceLineToJSON(sl)),
            clientInfos: wrapper.clientInfos,
            status: wrapper.opportunityStatus,
            salesFileStatus: wrapper.opportunitySalesFileStatus,
            type: wrapper.type,
            countryIsoCode: wrapper.countryIsoCode,
            standardPricingItemVersion: wrapper.standardPricingItemVersion,
            regionCode: wrapper.regionCode,
            regionLabel: wrapper.regionLabel,
            teamCode: wrapper.teamCode,
            teamLabel: wrapper.teamLabel,
            roleCode: wrapper.employeeRole.eForceRole,
            employee: currentUserValue,
            opportunityLabel: wrapper.opportunityLabel,
            opportunityId: wrapper.opportunityId,
            opportunityType: wrapper.opportunityType,
            opportunityOfflineId: wrapper.opportunityOfflineId,
            isMainQuote: wrapper.isMainQuote,
            nonNegociableArticleColor: wrapper.nonNegociableArticleColor,
            currency: wrapper.currency,
            color: wrapper.color,
            existingColor: wrapper.existingColor,
            eforceOpportunityId: wrapper.eforceOpportunityId,
            opportunityCreationDate: wrapper.opportunityCreationDate,
            speciality: wrapper.speciality,
            clientSize: wrapper.clientSize,
            contractId: wrapper.contractId,
            contractReference: wrapper.contractReference,
            nonReferencedCmcProtocolCode: wrapper.nonReferencedCmcProtocolCode,
            isSynchronized: undefined,
            locale: wrapper.locale
        };
        if (wrapper instanceof CmcQuote) {
            serializedWrapper['cmcProtocolCode'] = wrapper.cmcProtocolCode;
            serializedWrapper['cmcProtocolName'] = wrapper.cmcProtocolName;
            serializedWrapper['cmcProtocolComment'] = wrapper.cmcProtocolComment;
            serializedWrapper['cmcUniqueReference'] = wrapper.cmcUniqueReference;
        }
        return serializedWrapper;
    }

    public static quoteToQuoteWrapper(json: any) {
        let wrapper = new QuoteWrapper().copyWith({
            id: json.id,
            reference: json.reference,
            serviceLines: json.serviceLines.map(sl => QuoteManagerService.jsonToServiceLine(sl, json.cmcProtocolCode)),
            clientInfos: json.clientInfos,
            opportunityStatus: json.status,
            type: json.type,
            creationDate: json.creationDate,
            countryIsoCode: json.countryIsoCode,
            standardPricingItemVersion: json.standardPricingItemVersion,
            regionCode: json.employee.regionCode,
            regionLabel: json.employee.regionLabel,
            teamCode: json.employee.teamCode,
            teamLabel: json.employee.teamLabel,
            employeeRole: EmployeeRoleEnum.getEmployeeRoleEnumFromEforceRole(json.employee.roleCode),
            nonNegociableArticleColor: json.nonNegociableArticleColor,
            currency: json.currency,
            color: json.color,
            existingColor: json.existingColor,
            eforceOpportunityId: json.eforceOpportunityId,
            opportunityOfflineId: json.opportunityOfflineId,
            opportunityId: json.opportunityId,
            opportunityLabel: json.opportunityLabel,
            opportunityCreationDate: json.opportunityCreationDate,
            opportunitySalesFileStatus: json.salesFileStatus,
            opportunityType: json.opportunityType,
            contractId: json.contractId,
            contractReference: json.contractReference,
            clientSize: json.clientSize,
            locale: json.locale,
            speciality: json.speciality
        });
        if (json.type == EnumQuoteType.CMC) {
            wrapper = Object.setPrototypeOf(wrapper, CmcQuote.prototype).copyWith({
                cmcProtocolCode: json.cmcProtocolCode,
                cmcProtocolName: json.cmcProtocolName,
                cmcProtocolComment: json.cmcProtocolComment,
                cmcUniqueReference: json.cmcUniqueReference,
            });
        }
        return EPPClientPrototypeMapper.setQuoteWrapperPrototype(wrapper);
    }

    private static serviceLineToJSON(sl: ServiceLine) {
        return {
            code: sl.code,
            appliedPrice: sl.appliedPrice,
            appliedPriceColor: sl.appliedPriceColor,
            existingQuoteService: QuoteManagerService.quoteServiceToJSON(sl.existingQuoteService),
            renewalQuoteService: QuoteManagerService.quoteServiceToJSON(sl.renewalQuoteService),
            newQuoteService: QuoteManagerService.quoteServiceToJSON(sl.newQuoteService),
            isSelected: sl.isSelected,
            order: sl.order,
            isEnabled: sl.isEnabled,
            synthesisViews: sl.synthesisViews,
            displayMode: sl.displayMode,
            billingLines: sl.billingLines,
            weeklyAmount: sl.weeklyAmount,
            monthlyAmount: sl.monthlyAmount,
        };
    };

    private static quoteServiceToJSON(qs: QuoteService) {
        if(!qs){
            return null;
        }
        return {
            code: qs.code,
            contractInfoInputs: qs.contractInfoInputs,
            customGroups: qs.customGroups.map((customGroup) => {
                return this.customGroupToJSON(customGroup, EnumQuoteServiceType[qs.code]);
            }),
            appliedPrice: qs.appliedPrice,
            appliedPriceColor: qs.appliedPriceColor,
            appliedPriceDevices: qs.appliedPriceDevices,
            appliedPriceConsumables: qs.appliedPriceConsumables,
            appliedPriceDevicesColor: qs.appliedPriceDevicesColor,
            appliedPriceConsumablesColor: qs.appliedPriceConsumablesColor,
            isCmcFullyManaged: qs.isCmcFullyManaged,
            distance: qs.distance,
            isSelected: qs.isSelected,
            totalLogisticsPrice: qs.totalLogisticsPrice,
            factorVt: qs.factorVt,
            factorFreeMonth: qs.factorFreeMonth,
            weeklyAmount: qs.weeklyAmount,
            monthlyAmount: qs.monthlyAmount,
            serviceColor: qs.serviceColor,
            corridorArticles: qs.corridorDevices,
            corridorConsumables: qs.corridorConsumables,
            extraFees: qs.extraFees.filter(extraFee => extraFee.checked).map(extraFee => QuoteManagerService.extraFeesToJSON(extraFee)),
            discount: qs.discount,
            isCustomGroups: qs.isCustomGroups,
            pct: qs.pct,
            averagePctCoefficient: qs.averagePctCoefficient,
            displayMode: qs.displayMode,
            globalCorridor: qs.globalCorridor,
            additionalStockAmount: qs.additionalStockAmount,
            userInputs: qs.userInputs,
            consumablesDiscount: qs.consumablesDiscount,
            devicesDiscount: qs.devicesDiscount,
            salesType: qs.salesType
        };
    }

    private static customGroupToJSON(customGroup: CustomGroup, enumQuoteServiceType: EnumQuoteServiceType) {
        return {
            id: customGroup.id,
            title: customGroup.title,
            order: customGroup.order,
            totalAppliedPrice: customGroup.totalAppliedPrice,
            pricingItems: customGroup.pricingItems.map(pricingItem => this.pricingItemToJSON(pricingItem, enumQuoteServiceType))
        };
    }

    private static extraFeesToJSON(extraFee: ExtraFee) {
        return toExtraFeePayload(extraFee);
    }

    private static jsonToServiceLine(json: any, cmcProtocolCode: string) {
        return new ServiceLine().copyWith({
            code: json.code,
            appliedPrice: json.appliedPrice,
            appliedPriceColor: json.appliedPriceColor,
            isSelected: json.isSelected,
            order: json.order,
            isEnabled: true,
            existingQuoteService: this.jsonToQuoteService(json.existingQuoteService, cmcProtocolCode),
            renewalQuoteService: this.jsonToQuoteService(json.renewalQuoteService, cmcProtocolCode),
            newQuoteService: this.jsonToQuoteService(json.newQuoteService, cmcProtocolCode),
            billingLines: json.billingLines,
            displayMode: json.displayMode,
        });
    }

    private static jsonToQuoteService(json: any, cmcProtocolCode: string) {
        if (json) {
            return new QuoteService().copyWith({
                code: json.code,
                contractInfoInputs: json.contractInfoInputs,
                appliedPrice: json.appliedPrice,
                appliedPriceColor: json.appliedPriceColor,
                appliedPriceDevices: json.appliedPriceDevices,
                appliedPriceConsumables: json.appliedPriceConsumables,
                appliedPriceDevicesColor: json.appliedPriceDevicesColor,
                appliedPriceConsumablesColor: json.appliedPriceConsumablesColor,
                distance: json.distance,
                isSelected: json.isSelected,
                totalLogisticsPrice: json.totalLogisticsPrice,
                factorVt: json.factorVt,
                factorFreeMonth: json.factorFreeMonth,
                extraFees: json.extraFees.map(extraFee => QuoteManagerService.jsonToExtraFees(extraFee)),
                discount: json.discount,
                quoteType: cmcProtocolCode ? EnumQuoteType.CMC : EnumQuoteType.STANDARD,
                customGroups: json.customGroups.map(customGroup => {
                    customGroup.pricingItems = customGroup.pricingItems.map(pricingItem => this.jsonToPricingItem(pricingItem));
                    return customGroup;
                }),
                isCustomGroups: json.isCustomGroups,
                pct: json.pct,
                isCmcFullyManaged: json.isCmcFullyManaged,
                averagePctCoefficient: json.averagePctCoefficient,
                displayMode: json.displayMode,
                globalCorridor: json.globalCorridor,
                corridorDevices: json.corridorArticles,
                corridorConsumables: json.corridorConsumables,
                userInputs: json.userInputs,
                consumablesDiscount: json.consumablesDiscount,
                devicesDiscount: json.devicesDiscount,
                salesType: json.salesType,
                cmcProtocolCode: cmcProtocolCode,
            });
        } else {
            return null;
        }
    }

    private static pricingItemToJSON(pi: PricingItem, enumQuoteServiceType: EnumQuoteServiceType) {
        let pricingItem = {
            matchingId: pi.id,
            family: pi.family,
            userInputs: pi.userInputs,
            prefilledInputs: pi.prefilledInputs,
            pricingItemType: pi.pricingItemType,
            device: undefined,
            consumable: undefined,
            consumableExtraPrice: undefined,
            specificities: undefined,
            effTheo: undefined,
            discount: undefined,
            totalAppliedPriceColor: undefined,
            franchise: undefined,
            collectorExtraPrice: undefined,
            productType: pi.isCmc ? EnumQuoteType.CMC : EnumQuoteType.STANDARD,
            additionalConsumablePrice: undefined,
            totalAppliedPrice: pi.totalAppliedPrice,
            deviceConsumableCorridor: undefined,
            consoType: undefined,
            displayOrder: pi.displayOrder,
            parentId: undefined
        };

        if (pi instanceof DevicePricingItem) {
            pricingItem.device = QuoteManagerService.articleToJSON(pi.device);
            pricingItem.collectorExtraPrice = pi.collectorExtraPrice;
            // Add stock information
            let stock = {};
            if (enumQuoteServiceType === EnumQuoteServiceType.LS) {
                stock['initialStockLS'] = pi.initialStockLS;
                stock['initialstockBillLS'] = pi.initialstockBillLS;
            } else if (enumQuoteServiceType === EnumQuoteServiceType.VT) {
                stock['unitStockInitialVT'] = pi.unitStockInitialVT;
                stock['totalStockInitialVT'] = pi.totalStockInitialVT;
            }
            if (Object.keys(stock).length > 0) {
                pricingItem['stock'] = stock;
            }
        }

        if (pi instanceof DeviceConsumablePricingItem) {
            pricingItem.device = QuoteManagerService.articleToJSON(pi.device);
            pricingItem.consumable = QuoteManagerService.articleToJSON(pi.consumable);
            pricingItem.consumableExtraPrice = pi.consumableExtraPrice;
            pricingItem.specificities = pi.specificities;
            pricingItem.effTheo = pi.effTheo;
            pricingItem.discount = pi.discount;
            pricingItem.totalAppliedPriceColor = pi.totalAppliedPriceColor;
            pricingItem.franchise = pi.franchise;
        }

        if (pi instanceof DeviceConsumableWithSuppPricingItem) {
            pricingItem.device = QuoteManagerService.articleToJSON(pi.device);
            pricingItem.consumable = QuoteManagerService.articleToJSON(pi.consumable);
            pricingItem.consumableExtraPrice = pi.consumableExtraPrice;
            pricingItem.specificities = pi.specificities;
            pricingItem.effTheo = pi.effTheo;
            pricingItem.discount = pi.discount;
            pricingItem.totalAppliedPriceColor = pi.totalAppliedPriceColor;
            pricingItem.franchise = pi.franchise;
            pricingItem.additionalConsumablePrice = pi.additionalConsumablePrice;
            pricingItem.deviceConsumableCorridor = pi.deviceConsumableCorridor;
        }

        if (pi instanceof PackPricingItem) {
            pricingItem.device = QuoteManagerService.articleToJSON(pi.device);
        }
        if (pi instanceof OptionPricingItem) {
            pricingItem['granularity'] = pi.granularity;
            // pcc/catalogPrice/productPricings sont nécessaires pour recalculer le corridor
            // de l'option au rechargement (pas de produit en base pour les options)
            pricingItem['option'] = {
                ...QuoteManagerService.articleToJSON(pi.option),
            };
        }
        if (pi instanceof ConsumablePricingItem) {
            pricingItem.consumable = QuoteManagerService.articleToJSON(pi.consumable);
            pricingItem.consumableExtraPrice = pi.consumableExtraPrice;
            pricingItem.consoType = pi.consoType;
            pricingItem.parentId = pi.parentId;
        }
        return pricingItem;
    }

    private static articleToJSON(article: Article) {
        return {
            codeElis: article.codeElis,
            label: article.label,
            articleDetails: article.articleDetails,
            corridor: article.corridor,
            lastCorridorState: article.lastCorridorState,
            appliedPrice: article.appliedPrice,
            lastAppliedPriceState: article.oldAppliedPrice,
            appliedPriceColor: article.appliedPriceColor,
            discount: article.discount,
            status: article.status,
            distance: article.distance,
            groupingCode: article.groupingCode,
            existsInProductDb: article.existsInProductDb,
            isRenewal: article.isRenewal,
            blackPrice: article.blackPrice,
            greenPrice: article.greenPrice,
            logisticPrice: article.logisticPrice,
            pcc: article.pcc,
            ppe: article.ppe,
            catalogPrice: article?.catalogPrice,
            productPricings: article?.productPricings
        };
    }

    private static jsonToPricingItem(json: any) {
        let pricingItem = {
            id: generateUniqueId(),
            matchingId: json.matchingId,
            family: json.family ?? json.category,
            userInputs: json.userInputs,
            prefilledInputs: json.prefilledInputs,
            pricingItemType: json.pricingItemType,
            device: json.device,
            consumable: json.consumable,
            consumableExtraPrice: json.consumableExtraPrice,
            collectorExtraPrice: json.collectorExtraPrice,
            specificities: json.specificities,
            effTheo: json.effTheo,
            franchise: json?.franchise,
            isCmc: json.productType === EnumQuoteType.CMC,
            totalAppliedPrice: json.totalAppliedPrice,
            displayOrder: json.displayOrder
        };
        if (pricingItem.pricingItemType === EnumPricingItemType.DEVICE) {
            return new DevicePricingItem().copyWith(pricingItem);
        }
        if (pricingItem.pricingItemType === EnumPricingItemType.DEVICE_CONSUMABLE) {
            pricingItem['discount'] = json.discount;
            pricingItem['totalAppliedPriceColor'] = json.totalAppliedPriceColor;
            return new DeviceConsumablePricingItem().copyWith(pricingItem);
        }
        if (pricingItem.pricingItemType === EnumPricingItemType.DEVICE_CONSUMABLE_WITH_SUPP) {
            pricingItem['discount'] = json.discount;
            pricingItem['totalAppliedPriceColor'] = json.totalAppliedPriceColor;
            pricingItem['additionalConsumablePrice'] = json.additionalConsumablePrice;
            pricingItem['deviceConsumableCorridor'] = json.deviceConsumableCorridor;
            return new DeviceConsumableWithSuppPricingItem().copyWith(pricingItem);
        }
        if (pricingItem.pricingItemType === EnumPricingItemType.PACK) {
            return new PackPricingItem().copyWith(pricingItem);
        }
        if (pricingItem.pricingItemType === EnumPricingItemType.OPTION) {
            pricingItem['option'] = json.option;
            pricingItem['granularity'] = json.granularity;
            return new OptionPricingItem().copyWith(pricingItem);
        }
        if (pricingItem.pricingItemType === EnumPricingItemType.CONSUMABLE) {
            pricingItem['consoType'] = json.consoType;
            pricingItem['parentId'] = json.consoType;
            return new ConsumablePricingItem().copyWith(pricingItem);
        }
    }

    private static jsonToExtraFees(json: any) {
        let extraFee = {
            id: json.extraFeeId,
            label: json.label,
            processingMode: json.processingMode,
            uniteAmount: json.uniteAmount,
            percentage: json.percentage,
            type: json.type,
            serviceCode: json.serviceCode,
            country: json.country,
            role: json.role,
            defaultChecked: json.defaultChecked,
            checked: true,
            quantity: json.quantity,
            totalAmount: json.totalAmount
        };
        return new ExtraFee().copyWith(extraFee);
    }

    onCreateQuote(countryIsoCode: string) {
        this.initNewQuoteWrapperAndRedirectToQuoteTypePage(countryIsoCode);
    }


    createServiceLines(enabledQuoteServices: ServiceConfig[], synthesisViewConfig: SynthesisViewConfig[], employeeRole?: EmployeeRoleEnum): ServiceLine[] {
        let serviceLines = [];
        let _synthesisViews = this.getSynthesisViews(synthesisViewConfig);

        enabledQuoteServices.forEach(service => {
            const newQuoteService = this.quoteServiceService.mapServiceConfigToQuoteService(service, EnumQuoteServiceSalesType.NEW);
            const filteredDisplayMode = service.parameters.filter(param => param.key === 'displayMode');

            const serviceLineData: any = {
                code: service.serviceCode,
                order: service.order,
                isEnabled: service.isEnabled,
                displayMode: filteredDisplayMode.length > 0 ? filteredDisplayMode[0].value.displayMode : null,
                newQuoteService: newQuoteService,
                synthesisViews: _synthesisViews
            };

            if (employeeRole?.isDisco()) {
                serviceLineData.existingQuoteService = this.quoteServiceService.mapServiceConfigToQuoteService(service, EnumQuoteServiceSalesType.EXISTING);
                serviceLineData.renewalQuoteService = this.quoteServiceService.mapServiceConfigToQuoteService(service, EnumQuoteServiceSalesType.RENEWAL);
            }

            serviceLines.push(new ServiceLine().copyWith(serviceLineData));
        });
        return serviceLines;
    }


    filterAndAggregateCustomerContractsByServiceCode(enabledQuoteServices: ServiceConfig[], customerContracts: BillingLine[], allProductsByServiceRequest: ProductsByServicesRequest, cmcProductsByServiceRequest: ProductsByServicesRequest, cmcCode: string): Map<string, BillingLine[]> {
        const mapCustomerContractsByServiceCode = new Map();
        enabledQuoteServices.forEach(service => {
            const customerContractsByServiceCode = this.customerContractService.filterCustomerContractsByServiceCode(customerContracts, service.serviceCode);

            const aggregatedCustomerContractsByServiceCode = this.customerContractService.aggregateCustomerContractsByServiceCode(customerContractsByServiceCode,
                ['contractId', 'keyAccountCode', 'servicePricing', 'nbWearers', 'chargingType', 'serviceFrequency', 'articleCode', 'numberOfChanges', 'garmentStock', 'exchangeRule', 'finishingMethod', 'invoiceDate', 'unitPrice', 'unitPriceExtra', 'billingCategoryCode', 'articleType', 'articleFamily', 'articleName', 'relatedDeviceItemCode']);

            allProductsByServiceRequest.codesByService[service.serviceCode] = aggregatedCustomerContractsByServiceCode.map(cc => cc.articleCode);
            if (cmcCode) {
                cmcProductsByServiceRequest.codesByService[service.serviceCode] = aggregatedCustomerContractsByServiceCode.filter(cc => cc.keyAccountCode).map(cc => cc.articleCode);
            }

            mapCustomerContractsByServiceCode.set(service.serviceCode, aggregatedCustomerContractsByServiceCode);
        });
        return mapCustomerContractsByServiceCode;
    }

    createRenewalServiceLines(context: OpportunityContextModel, enabledQuoteServices: ServiceConfig[], customerContracts: BillingLine[], referential: any, synthesisViewConfig: SynthesisViewConfig[], clientSize: EnumClientSize, cmcProtocol: CmcProtocol, cmcCode: string): Observable<ServiceLine[]> {
        const countryIsoCode = context.requestBy.user.country;
        const role: EmployeeRoleEnum = EmployeeRoleEnum.getEmployeeRoleEnumFromEforceRole(context.requestBy.user.role);
        const market = context.opportunity.account.market;
        const subMarket = context.opportunity.account.subMarket;
        const region = context.requestBy.user.regionName;

        let serviceLines = [];

        const allProductsByServiceRequest = new ProductsByServicesRequest();
        allProductsByServiceRequest.initByCountryAndRole(countryIsoCode, role, market, subMarket, clientSize, EnumQuoteType.STANDARD, EnumOpportunityType.RENEWAL, null, true);

        const cmcProductsByServiceRequest = new ProductsByServicesRequest();


        if (cmcProtocol) {
            cmcProductsByServiceRequest.initByCountryAndRole(countryIsoCode, role, market, subMarket, clientSize, EnumQuoteType.CMC, EnumOpportunityType.RENEWAL, cmcProtocol.code, true);
        }

        const mapCustomerContractsByServiceCode = this.filterAndAggregateCustomerContractsByServiceCode(enabledQuoteServices, customerContracts, allProductsByServiceRequest, cmcProductsByServiceRequest, cmcProtocol?.code);

        let productSearchRequestV2 = new ProductSearchRequestV2(allProductsByServiceRequest.country, allProductsByServiceRequest.role, null,
            null, EnumQuoteServiceSalesType.RENEWAL, allProductsByServiceRequest.extinctionArticlesIncluded, market,
            region, null, clientSize, Array.from(new Set(Object.values(allProductsByServiceRequest.codesByService).flat()))
        );

        return forkJoin({
            standardProducts: this.productManagerV2Service.searchArticlesByFilteredElisCodesGrouped(productSearchRequestV2),
            cmcProducts: cmcProtocol ? this.cmcProductManagerService.searchArticlesForDuplication(cmcProductsByServiceRequest) : of([])
        }).pipe(map(response => {
            enabledQuoteServices.forEach((serviceConfig: ServiceConfig) => {
                const allProductsByService = response.standardProducts.find(sp => sp.service === serviceConfig.serviceCode)?.products;
                let cmcProductsByService = response.cmcProducts.length > 0 ? response.cmcProducts.find(cp => cp.service === serviceConfig.serviceCode)?.products : [];
                cmcProductsByService = cmcProductsByService.map(item => ({...item, isCmc: true}));

                let customGroups: CustomGroup[] = this.initRenewalPricingItems(allProductsByService, cmcProductsByService, serviceConfig, mapCustomerContractsByServiceCode, referential, !!cmcProtocol);

                const filteredDisplayMode = serviceConfig.parameters.filter(param => param.key === 'displayMode');

                let quoteServiceSales = this.quoteServiceService.mapServiceConfigToQuoteService(serviceConfig, EnumQuoteServiceSalesType.NEW);
                quoteServiceSales = quoteServiceSales.copyWith({
                    isCustomGroups: false,
                    nonNegociableArticleColor: EnumCorridorColor.LIGHTGREEN,
                    appliedPriceColor: EnumCorridorColor.GRAY,
                    appliedPrice: '0.00',
                    cmcProtocolCode: cmcCode
                });
                quoteServiceSales = this.quoteServiceService.calculateCorridors(quoteServiceSales, referential, serviceConfig, countryIsoCode, role);

                let quoteServiceExisting = this.quoteServiceService.mapServiceConfigToQuoteService(serviceConfig, EnumQuoteServiceSalesType.EXISTING);
                quoteServiceExisting = quoteServiceExisting.copyWith({
                    customGroups: customGroups,
                    nonNegociableArticleColor: EnumCorridorColor.LIGHTGREEN,
                    appliedPriceColor: EnumCorridorColor.GRAY,
                    appliedPrice: '0.00',
                    cmcProtocolCode: cmcCode

                });
                quoteServiceExisting = this.quoteServiceService.calculateCorridors(quoteServiceExisting, referential, serviceConfig, countryIsoCode, role);
                quoteServiceExisting = this.quoteServiceService.calculateAdditionalDetails(quoteServiceExisting);

                let quoteServiceRenewal = this.quoteServiceService.mapServiceConfigToQuoteService(serviceConfig, EnumQuoteServiceSalesType.RENEWAL);
                quoteServiceRenewal = quoteServiceRenewal.copyWith({
                    customGroups: customGroups,
                    nonNegociableArticleColor: EnumCorridorColor.LIGHTGREEN,
                    appliedPriceColor: EnumCorridorColor.GRAY,
                    appliedPrice: '0.00',
                    cmcProtocolCode: cmcCode
                });
                quoteServiceRenewal = this.quoteServiceService.calculateCorridors(quoteServiceRenewal, referential, serviceConfig, countryIsoCode, role);
                quoteServiceRenewal = this.quoteServiceService.calculateAdditionalDetails(quoteServiceRenewal);

                let serviceLine = new ServiceLine().copyWith({
                    code: serviceConfig.serviceCode,
                    order: serviceConfig.order,
                    isEnabled: serviceConfig.isEnabled,
                    existingQuoteService: quoteServiceExisting,
                    renewalQuoteService: quoteServiceRenewal,
                    newQuoteService: quoteServiceSales,
                    billingLines: this.customerContractService.filterCustomerContractsByServiceCode(customerContracts, serviceConfig.serviceCode),
                    appliedPriceColor: EnumCorridorColor.GRAY,
                    appliedPrice: '0.00',
                    displayMode: filteredDisplayMode.length > 0 ? filteredDisplayMode[0].value.displayMode : null,
                    synthesisViews: this.getSynthesisViews(synthesisViewConfig)
                });
                serviceLine = this.quoteServiceService.updateServiceLine(serviceLine);
                serviceLines.push(serviceLine);
            });
            return serviceLines;
        }));
    }

    private initRenewalPricingItems(products: ProductV2[], cmcProducts: ProductV2[], service: ServiceConfig, mapCustomerContractsByServiceCode: Map<string, BillingLine[]>, referential: any, isServiceCmc: boolean): CustomGroup[] {
        let customGroups: CustomGroup[] = [];

        const mapper = PricingItemMapperFactory.create(service.serviceCode);
        const pricingItems = mapper.mapPricingItemsFromCustomerContracts(mapCustomerContractsByServiceCode, service, products, cmcProducts, referential, isServiceCmc);

        customGroups.push(new CustomGroup().copyWith({
            id: generateUniqueId(),
            title: 'default',
            order: 1,
            pricingItems: pricingItems
        }));

        return customGroups;
    }


    initNewQuoteWrapperAndRedirectToQuoteTypePage(countryIsoCode: string) {
        const wrapper = this.initQuoteWrapper(countryIsoCode, EnumQuoteType.STANDARD, null);
        this.store.dispatch(new QuoteUpdateAction({wrapper: wrapper, executeEffects: true}));
        this.eppRouterService.redirectToQuoteType(false);
    }

    initQuoteWrapper(countryIsoCode: string, quoteType: EnumQuoteType, employeeRole: EmployeeRoleEnum): QuoteWrapper {
        return new QuoteWrapper().copyWith(
            {
                serviceLines: [],
                type: quoteType,
                employeeRole: employeeRole,
                standardPricingItemVersion: null,
                countryIsoCode: countryIsoCode
            });
    }

    setExtraFeesByService(extraFees: ExtraFee[], serviceLines: ServiceLine[]): ServiceLine[] {
        const newServiceLines: ServiceLine[] = [];
        const fees = extraFees.map(extraFee => {
            Object.setPrototypeOf(extraFee, ExtraFee.prototype);
            return extraFee.copyWith({checked: extraFee.defaultChecked ? true : false, quantity: 0});
        });
        serviceLines.forEach(serviceLine => {
            const filteredExtraFeesByService = fees.filter(extraFee => extraFee.serviceCode === serviceLine.code);
            if (filteredExtraFeesByService.length) {
                serviceLine = serviceLine.copyWith({newQuoteService: serviceLine.newQuoteService.copyWith({extraFees: filteredExtraFeesByService})});
                newServiceLines.push(serviceLine);
            } else {
                newServiceLines.push(serviceLine);
            }
        });
        return newServiceLines;
    }

    setServiceOptionsByService(options: Option[], serviceLines: ServiceLine[]): ServiceLine[] {
        const defaultCheckedServiceOptions = (options ?? []).filter(option =>
            option.granularity === EnumOptionGranularity.SERVICE && option.defaultChecked);

        return serviceLines.map(serviceLine => {
            const newQuoteService = serviceLine.newQuoteService;
            if (!newQuoteService) {
                return serviceLine;
            }

            // VT: defer SERVICE option injection to the effect, which checks for non-option items first
            if (serviceLine.code === 'VT') {
                return serviceLine;
            }

            const existingCodes = new Set(newQuoteService.options.map(optionPricingItem => optionPricingItem.option?.codeElis));
            const optionsToAdd = defaultCheckedServiceOptions
                .filter(option => option.serviceCode === serviceLine.code && !existingCodes.has(option.code));

            if (optionsToAdd.length === 0) {
                return serviceLine;
            }

            const defaultCustomGroup = newQuoteService.customGroups.length > 0
                ? newQuoteService.customGroups[0]
                : new CustomGroup().copyWith({id: generateUniqueId(), title: 'default', order: 1, pricingItems: []});

            const newPricingItems = [
                ...defaultCustomGroup.pricingItems,
                ...optionsToAdd.map(option => this.optionPricingItemMapper.getPricingItemFromOption(option, 0)
                    .copyWith({granularity: EnumOptionGranularity.SERVICE}))
            ];

            const newCustomGroups = newQuoteService.customGroups.length > 0
                ? newQuoteService.customGroups.map(customGroup =>
                    customGroup.id === defaultCustomGroup.id
                        ? customGroup.copyWith({pricingItems: newPricingItems})
                        : customGroup)
                : [defaultCustomGroup.copyWith({pricingItems: newPricingItems})];

            return serviceLine.copyWith({newQuoteService: newQuoteService.copyWith({customGroups: newCustomGroups})});
        });
    }

    getSynthesisViews(synthesisViewConfig: SynthesisViewConfig[]): any[] {

        let synthesisViews = [];
        synthesisViewConfig.forEach(view => {
            synthesisViews.push({
                label: 'LABEL_' + view.name.toUpperCase() + '_VIEW',
                code: view.name,
                selected: false
            });
        });
        if (synthesisViews.length) {
            synthesisViews[0].selected = true;
        }

        return synthesisViews;
    }

}
